/**
 * Журнал пакетов `/history` (§8).
 *
 * Слева колонка фильтров 240 px, справа таблица пакетов по 50 на страницу.
 * Строка раскрывается в тот же дифф, что на ревью и в предпросмотре Telegram —
 * один компонент `ChangeDiff`, чтобы в чате, на ревью и в журнале человек видел
 * одинаковую картину.
 *
 * Два места, где интерфейс говорит правду вместо красивой заглушки:
 *  · отбор по статусу пакета сервер не умеет — он идёт по загруженной странице,
 *    и под таблицей об этом написано;
 *  · откат сервер применяет сразу отдельным пакетом, а не наполняет черновик,
 *    поэтому модалка обещает публикацию, а не черновик.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ChangeSetDetail, ChangeSetSummary } from '@shared/types.ts'
import {
  Button, ConsequenceLine, EmptyState, IconChevronLeft, IconChevronRight, Modal, Skeleton, Tooltip,
} from '../../components/index.js'
import { api, ApiRequestError } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { ChangeSetRow } from './ChangeSetRow.js'
import {
  HistoryFilters, emptyHistoryFilter, historyFilterFromParams, historyFilterIsEmpty,
  historyFilterToParams, historyServerParams, historyStatusIsClientSide, matchesHistoryStatus,
  type HistoryFilter,
} from './HistoryFilters.js'
import { countOverwritten } from './revert-impact.js'
import { historyTexts } from './texts.history.js'
import './HistoryScreen.css'

const PAGE_SIZE = 50
const COLUMNS = 8

type LoadState = 'loading' | 'ready' | 'error'

export function HistoryScreen() {
  const [params, setParams] = useSearchParams()
  const bootstrap = useApp((s) => s.bootstrap)
  const brandsById = useApp((s) => s.brandsById)
  const toast = useApp((s) => s.toast)
  const reloadBrands = useApp((s) => s.reloadBrands)

  const permissions = bootstrap?.user.permissions ?? []
  const canRevert = permissions.includes('changesets.revert')
  const canNotify = permissions.includes('settings.manage')
  const currentUserId = bootstrap?.user.id ?? 0

  const parsed = useMemo(() => historyFilterFromParams(params), [params])
  const filter = parsed.filter

  /** Ссылка на выгрузку журнала: период берём из тех же фильтров экрана. */
  const logHref = (format: 'csv' | 'xlsx'): string => {
    const params = historyServerParams(filter, currentUserId, 1)
    const search = new URLSearchParams()
    if (typeof params.from === 'string') search.set('from', params.from)
    if (typeof params.to === 'string') search.set('to', params.to)
    const tail = search.toString()
    return `/api/history/export.${format}${tail ? `?${tail}` : ''}`
  }
  const page = parsed.page

  const [items, setItems] = useState<ChangeSetSummary[]>([])
  const [total, setTotal] = useState(0)
  const [state, setState] = useState<LoadState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [details, setDetails] = useState<Map<number, ChangeSetDetail>>(new Map())
  const [detailErrors, setDetailErrors] = useState<Map<number, string>>(new Map())
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set())

  const [revertTarget, setRevertTarget] = useState<ChangeSetSummary | null>(null)
  const [reverting, setReverting] = useState(false)
  const [notifying, setNotifying] = useState<Set<number>>(new Set())

  const serverParams = useMemo(
    () => historyServerParams(filter, currentUserId, page),
    [filter, currentUserId, page],
  )

  // Ключ запроса: пересобираем ленту только когда поменялись параметры сервера.
  const requestKey = JSON.stringify(serverParams)

  useEffect(() => {
    let alive = true
    setState('loading')
    setError(null)
    api.changeSets(serverParams)
      .then((res) => {
        if (!alive) return
        setItems(res.items)
        setTotal(res.total)
        setState('ready')
      })
      .catch((err: unknown) => {
        if (!alive) return
        setError(err instanceof Error ? err.message : historyTexts.errorTitle)
        setState('error')
      })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, reloadToken])

  const applyFilter = useCallback((next: HistoryFilter) => {
    setExpanded(new Set())
    setParams(historyFilterToParams(next, 1), { replace: false })
  }, [setParams])

  const goToPage = useCallback((next: number) => {
    setExpanded(new Set())
    setParams(historyFilterToParams(filter, next))
  }, [filter, setParams])

  const loadDetail = useCallback(async (id: number) => {
    setLoadingDetails((prev) => new Set(prev).add(id))
    try {
      const detail = await api.changeSet(id)
      setDetails((prev) => new Map(prev).set(id, detail))
      setDetailErrors((prev) => {
        const next = new Map(prev)
        next.delete(id)
        return next
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : historyTexts.errorTitle
      setDetailErrors((prev) => new Map(prev).set(id, message))
    } finally {
      setLoadingDetails((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }, [])

  const toggleRow = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else {
        next.add(id)
        if (!details.has(id)) void loadDetail(id)
      }
      return next
    })
  }, [details, loadDetail])

  const notifyAgain = useCallback(async (item: ChangeSetSummary) => {
    setNotifying((prev) => new Set(prev).add(item.id))
    try {
      await api.notifyAgain(item.id)
      toast('success', historyTexts.telegram.sentToast)
      setReloadToken((n) => n + 1)
    } catch (err: unknown) {
      toast('error', err instanceof ApiRequestError ? err.message : historyTexts.errorTitle)
    } finally {
      setNotifying((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
    }
  }, [toast])

  const openRevert = useCallback((item: ChangeSetSummary) => {
    setRevertTarget(item)
    if (!details.has(item.id)) void loadDetail(item.id)
  }, [details, loadDetail])

  const confirmRevert = useCallback(async () => {
    const target = revertTarget
    if (!target) return
    setReverting(true)
    try {
      const res = await api.revert(target.id)
      toast('success', historyTexts.revert.done(res.changeSetId, target.id))
      setRevertTarget(null)
      setReloadToken((n) => n + 1)
      await reloadBrands()
    } catch (err: unknown) {
      // Системный пакет, пакет справочников и незакрытый черновик — сервер
      // объясняет отказ по-русски, своих формулировок сверху не придумываем.
      toast('error', err instanceof ApiRequestError ? err.message : historyTexts.errorTitle)
      setRevertTarget(null)
    } finally {
      setReverting(false)
    }
  }, [revertTarget, toast, reloadBrands])

  const shown = useMemo(
    () => items.filter((item) => matchesHistoryStatus(item, filter.status)),
    [items, filter.status],
  )

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to = Math.min(page * PAGE_SIZE, total)

  const revertDetail = revertTarget ? details.get(revertTarget.id) ?? null : null
  const overwritten = revertDetail ? countOverwritten(revertDetail, brandsById) : 0

  return (
    <div className="history">
      <HistoryFilters value={filter} onChange={applyFilter} currentUserId={currentUserId} />

      <div className="history__main">
        <header className="history__head">
          <h1 className="t-h1">{historyTexts.title}</h1>
          {/* Выгрузка журнала за выбранный период: те же from/to, что у списка,
              поэтому файл совпадает с тем, что человек видит на экране. */}
          <span className="history__export">
            <a className="t-btn history__export-link" href={logHref('xlsx')} download>
              {historyTexts.exportXlsx}
            </a>
            <a className="t-btn history__export-link" href={logHref('csv')} download>
              {historyTexts.exportCsv}
            </a>
          </span>
        </header>

        {state === 'loading' ? (
          <Skeleton rows={12} columns={[64, 132, 180, 240, 80, 96, 120]} label={historyTexts.loadingLabel} />
        ) : state === 'error' ? (
          <EmptyState
            title={historyTexts.errorTitle}
            text={error}
            actions={<Button variant="primary" onClick={() => setReloadToken((n) => n + 1)}>{historyTexts.retry}</Button>}
          />
        ) : shown.length === 0 ? (
          <EmptyState
            title={historyTexts.emptyTitle}
            actions={
              historyFilterIsEmpty(filter)
                ? undefined
                : <Button variant="secondary" onClick={() => applyFilter(emptyHistoryFilter)}>{historyTexts.emptyReset}</Button>
            }
          />
        ) : (
          <>
            <table className="history__table">
              <colgroup>
                <col className="history__col--id" />
                <col className="history__col--when" />
                <col className="history__col--author" />
                <col className="history__col--note" />
                <col className="history__col--channel" />
                <col className="history__col--counts" />
                <col className="history__col--tg" />
                <col className="history__col--actions" />
              </colgroup>
              <thead>
                <tr className="history__headrow">
                  <th className="history__th t-col" scope="col">{historyTexts.columns.number}</th>
                  <th className="history__th t-col" scope="col">{historyTexts.columns.when}</th>
                  <th className="history__th t-col" scope="col">{historyTexts.columns.author}</th>
                  <th className="history__th t-col" scope="col">{historyTexts.columns.note}</th>
                  <th className="history__th t-col" scope="col">{historyTexts.columns.channel}</th>
                  <th className="history__th history__th--num t-col" scope="col">{historyTexts.columns.changes}</th>
                  <th className="history__th t-col" scope="col">{historyTexts.columns.telegram}</th>
                  <th className="history__th" scope="col">
                    <span className="visually-hidden">{historyTexts.columns.actions}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => (
                  <ChangeSetRow
                    key={item.id}
                    item={item}
                    columns={COLUMNS}
                    expanded={expanded.has(item.id)}
                    onToggle={() => toggleRow(item.id)}
                    detail={details.get(item.id) ?? null}
                    detailState={
                      loadingDetails.has(item.id)
                        ? 'loading'
                        : detailErrors.has(item.id) ? 'error' : 'idle'
                    }
                    detailError={detailErrors.get(item.id) ?? null}
                    highlight={filter.brandQuery}
                    fallbackChannel={filter.channel}
                    onPickAuthor={(authorId) => applyFilter({ ...filter, author: authorId })}
                    canRevert={canRevert}
                    onRevert={openRevert}
                    canNotify={canNotify}
                    notifying={notifying.has(item.id)}
                    onNotifyAgain={(target) => void notifyAgain(target)}
                  />
                ))}
              </tbody>
            </table>

            <footer className="history__foot">
              <span className="history__shown t-meta" aria-live="polite">
                {historyTexts.shown(from, to, total)}
                {historyStatusIsClientSide(filter)
                  ? ` · ${historyTexts.shownFiltered(shown.length, items.length)}`
                  : ''}
              </span>
              <span className="history__pager">
                <Button
                  variant="secondary"
                  size="s"
                  disabled={page <= 1}
                  iconBefore={<IconChevronLeft />}
                  aria-label={historyTexts.prevPage}
                  onClick={() => goToPage(page - 1)}
                >
                  {historyTexts.prev}
                </Button>
                <span className="history__page t-meta">{historyTexts.pageOf(page, pages)}</span>
                <Button
                  variant="secondary"
                  size="s"
                  disabled={page >= pages}
                  iconAfter={<IconChevronRight />}
                  aria-label={historyTexts.nextPage}
                  onClick={() => goToPage(page + 1)}
                >
                  {historyTexts.next}
                </Button>
              </span>
            </footer>
          </>
        )}
      </div>

      <Modal
        open={revertTarget !== null}
        onClose={() => setRevertTarget(null)}
        title={revertTarget ? historyTexts.revert.title(revertTarget.id) : ''}
        size="s"
        footerNote={<ConsequenceLine tone="danger">{historyTexts.revert.consequence}</ConsequenceLine>}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevertTarget(null)}>
              {historyTexts.revert.cancel}
            </Button>
            <Button variant="primary" loading={reverting} onClick={() => void confirmRevert()}>
              {historyTexts.revert.confirm}
            </Button>
          </>
        }
        onConfirm={() => void confirmRevert()}
      >
        {revertTarget ? (
          <p className="history__revert t-body">
            {historyTexts.revert.counts(revertTarget.id, revertTarget.changesCount)}
            {overwritten > 0 ? ` ${historyTexts.revert.overwritten(overwritten)}` : ''}
          </p>
        ) : null}
      </Modal>
    </div>
  )
}
